import { app } from 'app/providers/FirebaseProvider/ui/FirebaseProvider'
import { getAuth } from 'firebase/auth'
import { useEffect, useState } from 'react'
import { getEmailLink } from 'shared/api/emailAuth'
import { Button } from 'shared/ui/Button'
import { ColorEnum } from 'shared/ui/Button/ui/Button'
import { BottomSheet } from 'shared/ui/BottomSheet'
import { CloseIcon } from 'shared/ui/Icon'
import { Text, TextAlign, TextWeight } from 'shared/ui/Text/Text'
import { emailAuthEnabled } from '../config'
import { EmailAuthFlow } from './EmailAuthFlow'
import styles from './AccountEmailBanner.module.scss'

const dismissedStorageKey = (profileId) =>
    `minecards:account-banner-dismissed:${profileId}`

export const AccountEmailBanner = ({
    onAccountChanged,
    onAccountStateChange,
}) => {
    const [state, setState] = useState('loading')
    const [account, setAccount] = useState(null)
    const [dialog, setDialog] = useState(null)

    useEffect(() => {
        if (!emailAuthEnabled) {
            setState('hidden')
            return
        }
        const user = getAuth(app).currentUser
        let active = true
        getEmailLink(user)
            .then((result) => {
                if (active) {
                    setAccount(result.account ?? null)
                    const nextState = result.linked ? 'linked' : 'unlinked'
                    onAccountStateChange?.(nextState)
                    if (
                        result.linked &&
                        window.localStorage.getItem(
                            dismissedStorageKey(result.account?.profileId)
                        ) === 'true'
                    ) {
                        setState('hidden')
                    } else {
                        setState(nextState)
                    }
                }
            })
            .catch(() => {
                if (active) {
                    onAccountStateChange?.('unknown')
                    setState('hidden')
                }
            })
        return () => {
            active = false
        }
    }, [onAccountStateChange])

    if (state !== 'unlinked' && state !== 'linked') {
        return null
    }

    const user = getAuth(app).currentUser
    const linked = state === 'linked'
    const dialogLabel = {
        link: 'Привязать почту',
        login: 'Войти в аккаунт',
        'switch-warning': 'Войти в другой аккаунт',
    }[dialog]

    const openAccountLogin = () => {
        setDialog(linked ? 'login' : 'switch-warning')
    }

    const dismiss = () => {
        if (account?.profileId) {
            window.localStorage.setItem(
                dismissedStorageKey(account.profileId),
                'true'
            )
        }
        setState('hidden')
    }

    return (
        <>
            <section className={styles.banner}>
                {linked && (
                    <button
                        aria-label="Скрыть сообщение об аккаунте"
                        className={styles.dismissButton}
                        onClick={dismiss}
                        type="button"
                    >
                        <CloseIcon size={20} />
                    </button>
                )}
                <div className={styles.copy}>
                    <Text
                        align={TextAlign.LEFT}
                        className={styles.title}
                        weight={TextWeight.BOLD}
                    >
                        {linked ? 'Аккаунт сохранён' : 'Сохрани аккаунт'}
                    </Text>
                    <Text align={TextAlign.LEFT} className={styles.text}>
                        {linked
                            ? `В этот профиль можно войти по почте ${account?.email ?? ''}.`
                            : 'Привяжи почту, чтобы войти в этот профиль в другом браузере или PWA.'}
                    </Text>
                </div>
                <div className={styles.actions}>
                    {!linked && (
                        <Button
                            className={styles.button}
                            color={ColorEnum.BLUE}
                            onClick={() => setDialog('link')}
                            text="Привязать почту"
                        />
                    )}
                    <button
                        className={styles.switchButton}
                        onClick={openAccountLogin}
                        type="button"
                    >
                        {linked
                            ? 'Войти в другой аккаунт'
                            : 'Уже есть аккаунт? Войти'}
                    </button>
                </div>
            </section>
            <BottomSheet
                label={dialogLabel || 'Аккаунт'}
                onOpenChange={(open) => !open && setDialog(null)}
                open={Boolean(dialog)}
            >
                {dialog === 'link' ? (
                    <EmailAuthFlow
                        mode="link"
                        onBack={() => setDialog(null)}
                        onComplete={(result) => {
                            setAccount(result)
                            setState('linked')
                            onAccountStateChange?.('linked')
                            setDialog(null)
                        }}
                        user={user}
                    />
                ) : null}
                {dialog === 'switch-warning' ? (
                    <div className={styles.warning}>
                        <Text
                            align={TextAlign.LEFT}
                            className={styles.warningTitle}
                            weight={TextWeight.BOLD}
                        >
                            Войти в другой аккаунт?
                        </Text>
                        <Text
                            align={TextAlign.LEFT}
                            className={styles.warningText}
                        >
                            Этот профиль пока не привязан к почте. Его данные не
                            удалятся, но после переключения вернуться к нему без
                            привязанной почты не получится.
                        </Text>
                        <Button
                            className={styles.warningButton}
                            onClick={() => setDialog('link')}
                            text="Сначала привязать почту"
                        />
                        <button
                            className={styles.switchButton}
                            onClick={() => setDialog('login')}
                            type="button"
                        >
                            Всё равно войти
                        </button>
                    </div>
                ) : null}
                {dialog === 'login' ? (
                    <EmailAuthFlow
                        mode="login"
                        onBack={() => setDialog(null)}
                        onComplete={(result) => {
                            setDialog(null)
                            onAccountChanged?.(result)
                        }}
                    />
                ) : null}
            </BottomSheet>
        </>
    )
}
